import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {find, findIndex, get, isEmpty, join, map} from 'lodash';
import React, {useEffect, useLayoutEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {history} from "common/history";
import {PAGE_HEIGHT_WITH_NAVBAR} from "common/helpers";
import {color, font, radius, space, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import CtaButton from "components/redesign/CtaButton";
import Question from "components/Question";
import questionFields from "components/redesign/questionFields";
import Skeleton from "components/redesign/Skeleton";

const useStyles = makeStyles({
  page: {
    background: color.SURFACE,
    height: PAGE_HEIGHT_WITH_NAVBAR,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '0 32px',
    boxSizing: 'border-box',
  },
  column: {
    width: 638,
    maxWidth: '100%',
    margin: '0 auto',
  },
  topBar: {
    flexShrink: 0,
    background: color.SURFACE,
    paddingBottom: 18,
  },
  eyebrow: {
    ...type.eyebrow,
    color: color.TEXT_MUTED,
    margin: '14px 0 0',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '6px 0 16px',
  },
  back: {
    ...type.body,
    fontSize: 18,
    lineHeight: '26px',
    fontWeight: font.SEMIBOLD,
    color: color.TEXT,
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    gap: 10,
    flex: '1 1 0',
    minWidth: 0,
    cursor: 'pointer',
  },
  backArrow: {
    color: color.TEXT_MUTED,
    fontSize: 20,
    lineHeight: 1,
    flexShrink: 0,
  },
  remaining: {
    ...type.meta,
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
    borderRadius: radius.PILL,
    padding: '5px 14px',
    margin: 0,
    flexShrink: 0,
  },
  progressTrack: {
    height: 4,
    borderRadius: 4,
    background: color.DIVIDER,
    overflow: 'hidden',
  },
  progressFill: {
    display: 'block',
    height: '100%',
    borderRadius: 4,
    background: color.ACCENT,
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowAnchor: 'none',
  },
  content: {
    padding: '24px 0 72px',
  },
  pageHeader: {
    marginBottom: 24,
  },
  pageTitle: {
    ...type.panelTitle,
    color: color.TEXT,
    margin: 0,
  },
  pageDescription: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '8px 0 0',
  },
  questions: questionFields,
  empty: {
    ...type.body,
    color: color.TEXT_MUTED,
    textAlign: 'center',
    margin: '80px 0',
  },
  footer: {
    flexShrink: 0,
    borderTop: `1px solid ${color.DIVIDER}`,
  },
  footerInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
  },
  spacer: {
    flex: 1,
  },
  contentSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingTop: 24,
  },
});

const answerFor = (question, answers) => get(answers, question.key);
const errorFor = (question, errors) => join(get(errors, question.key), '\n');

export default function Stage({
  location,
  initStage,
  stage,
  survey,
  nextPage,
  previousPage,
  setAnswer,
  submitSurveyStage,
  getUserStageExistingAnswers,
}) {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();
  const {stageId} = get(location, 'state', {});

  const {currentPage, totalPages, errors, answers, fetchingStage, isSurveySubmitting} = stage;
  const existingAnswers = get(stage, 'userExistingAnswers', []);

  useEffect(() => {
    initStage(stageId);
    getUserStageExistingAnswers(stageId);
  }, [stageId]);

  // The stage in the store can still be the one a previous visit left behind,
  // so nothing derived from it is read until this stage's own pages are in.
  const isFirstLoad = fetchingStage || get(stage, 'stage.stageId') !== stageId;
  const loadedStage = isFirstLoad ? undefined : get(stage, 'stage');

  const stages = get(survey, 'stages', []);
  const stageIndex = findIndex(stages, s => s.id === stageId);
  const currentStage = find(stages, s => s.id === stageId);
  const stageName = st(
    get(currentStage, 'name', get(loadedStage, 'name')),
    get(currentStage, 'localeName', get(loadedStage, 'localeName'))
  );

  const currentPageItem = get(loadedStage, `pages[${currentPage}]`);
  const questions = get(currentPageItem, 'questions', []);
  const isLastPage = totalPages === currentPage + 1;

  const isMultiQuestionForm = map(get(loadedStage, 'pages', []), page =>
    get(page, 'questions', []).length).some(count => count !== 1);
  const remainingCount = totalPages - currentPage;
  const remaining = isMultiQuestionForm
    ? `${remainingCount} ${remainingCount === 1 ? t('stepLeft') : t('stepsLeft')}`
    : `${remainingCount} ${remainingCount === 1 ? t('questionLeft') : t('questionsLeft')}`;

  const answeredKeys = get(existingAnswers, '[0].answers', []).map(answer => answer.questionKey);
  const questionsToShow = questions.filter(question => !answeredKeys.includes(question.key));

  const goBack = () => {
    if (currentPage === 0) {
      history.goBack();
    } else {
      previousPage();
    }
  };

  const scrollerRef = useRef(null);
  useLayoutEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [currentPage]);

  // Continue leaves the page in place when something is missing, and the
  // question at fault can be well off the bottom of a long step.
  useEffect(() => {
    const firstError = scrollerRef.current?.querySelector('[role="alert"]');
    firstError?.parentElement.scrollIntoView({behavior: 'smooth', block: 'center'});
  }, [errors]);

  const pageTitle = st(get(currentPageItem, 'title'), get(currentPageItem, 'localeTitle'));
  const pageDescription = st(get(currentPageItem, 'description'), get(currentPageItem, 'localeDescription'));

  return (
    <div className={classes.page}>
      <div className={classes.topBar}>
        <div className={classes.container}>
          <div className={classes.column}>
            {stages.length > 1 && (
              <p className={classes.eyebrow}>
                {t('stageProgress', {current: stageIndex + 1, total: stages.length})}
              </p>
            )}
            <div className={classes.titleRow}>
              <button type={'button'} className={classes.back} onClick={goBack}>
                <span className={classes.backArrow} aria-hidden={true}>{'←'}</span>
                {isFirstLoad ? <Skeleton width={200} height={22}/> : stageName}
              </button>
              {!isFirstLoad && Boolean(totalPages) && (
                <p className={classes.remaining}>{remaining}</p>
              )}
            </div>
            <div className={classes.progressTrack}>
              {!isFirstLoad && Boolean(totalPages) && (
                <span
                  className={classes.progressFill}
                  style={{width: `${((currentPage + 1) * 100) / totalPages}%`}}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.scroller} ref={scrollerRef}>
        <div className={classes.container}>
          {isFirstLoad ? (
            <div className={classNames(classes.column, classes.contentSkeleton)}>
              <Skeleton width={'55%'} height={28}/>
              <Skeleton height={52}/>
              <Skeleton height={52}/>
              <Skeleton width={'70%'} height={16}/>
            </div>
          ) : (
            <div className={classNames(classes.column, classes.content)}>
              {(Boolean(pageTitle) || Boolean(pageDescription)) && (
                <div className={classes.pageHeader}>
                  {Boolean(pageTitle) && <p className={classes.pageTitle}>{pageTitle}</p>}
                  {Boolean(pageDescription) && (
                    <p className={classes.pageDescription}>{pageDescription}</p>
                  )}
                </div>
              )}
              {isEmpty(questionsToShow) && !isSurveySubmitting ? (
                <p className={classes.empty}>{t('noQuestionsInStep')}</p>
              ) : (
                <div className={classes.questions}>
                  {map(questionsToShow, question => (
                    <Question
                      key={question.key}
                      question={question}
                      onAnswered={answer => setAnswer(answer, question)}
                      value={answerFor(question, answers)}
                      error={errorFor(question, errors)}
                      showBorder
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!isFirstLoad && (
        <div className={classes.footer}>
          <div className={classes.container}>
            <div className={classNames(classes.column, classes.footerInner)}>
              {currentPage > 0 && (
                <CtaButton
                  size={'dense'}
                  variant={'secondary'}
                  label={`← ${t('previous')}`}
                  onClick={() => previousPage()}
                />
              )}
              <span className={classes.spacer}/>
              <CtaButton
                size={'dense'}
                variant={'primary'}
                label={isLastPage ? t('submitCaps') : t('continueCaps')}
                loading={isSurveySubmitting}
                onClick={() => isLastPage
                  ? submitSurveyStage(currentPageItem, answers, existingAnswers)
                  : currentPageItem && nextPage(currentPageItem, answers, existingAnswers)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
